import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Logo, ACCENT } from "@/platform/components/primitives";

// A small, believable Pinlayr SaaS UI preview: a browser canvas with a
// numbered pin, a dark ticket panel, and a hint of the floating toolbar.
function PinlayrPreview() {
  return (
    <div className="relative mx-auto w-full max-w-[420px] select-none" aria-hidden="true">
      {/* Browser window */}
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d0d] shadow-2xl">
        <div className="flex items-center gap-2 border-b border-white/10 bg-[#1a1a1a] px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
          <div className="ml-3 h-5 flex-1 rounded-md bg-[#151A24]/[0.06]" />
        </div>
        {/* Website canvas */}
        <div className="relative bg-[#f7f7f5] p-5">
          <div className="h-3 w-1/3 rounded bg-[#e2e2df]" />
          <div className="mt-3 h-2 w-3/4 rounded bg-[#ececea]" />
          <div className="mt-2 h-2 w-2/3 rounded bg-[#ececea]" />
          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className="h-16 rounded-lg bg-[#ececea]" />
            <div className="h-16 rounded-lg bg-[#ececea]" />
            <div className="h-16 rounded-lg bg-[#ececea]" />
          </div>

          {/* Numbered pin */}
          <div
            className="absolute left-[62%] top-[38%] grid h-7 w-7 place-items-center rounded-full text-[13px] font-bold shadow-lg ring-2 ring-white"
            style={{ background: ACCENT, color: "#171717" }}
          >
            1
          </div>
        </div>
      </div>

      {/* Floating dark ticket panel */}
      <div className="absolute -bottom-6 -right-3 w-[210px] rounded-xl border border-white/10 bg-[#1d1d1d] p-3.5 shadow-2xl">
        <div className="flex items-center gap-2">
          <span className="grid h-5 w-5 place-items-center rounded-full text-[10px] font-bold" style={{ background: ACCENT, color: "#171717" }}>1</span>
          <span className="text-[11px] font-medium text-white/80">Alignment looks off here</span>
        </div>
        <div className="mt-2.5 flex items-center gap-2">
          <span className="h-5 w-5 rounded-full bg-[#0079BF]" />
          <div className="h-2 flex-1 rounded bg-[#151A24]/10" />
        </div>
        <div className="mt-2 h-2 w-2/3 rounded bg-[#151A24]/10" />
      </div>

      {/* Toolbar pill */}
      <div className="absolute -left-3 bottom-8 flex items-center gap-1.5 rounded-full border border-white/10 bg-[#1d1d1d] px-2.5 py-1.5 shadow-xl">
        <span className="h-2 w-2 rounded-full" style={{ background: ACCENT }} />
        <span className="h-1.5 w-4 rounded bg-[#151A24]/20" />
        <span className="h-1.5 w-4 rounded bg-[#151A24]/20" />
      </div>
    </div>
  );
}

const BENEFITS = [
  "Pin feedback directly on live websites",
  "Turn comments into trackable tickets",
  "Invite reviewers without another account",
];

function ProductPanel() {
  return (
    <div className="relative hidden flex-col justify-between overflow-hidden bg-[#111111] p-10 lg:flex xl:p-14">
      {/* subtle glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(70% 50% at 70% 10%, rgba(124,107,255,0.12) 0%, rgba(17,17,17,0) 60%)" }}
      />
      <div className="relative">
        <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT }}>
          14-day free trial
        </span>
        <h2 className="mt-4 text-[30px] font-semibold leading-tight tracking-tight text-[#F4F5F8]">
          Try Pinlayr free for 14 days
        </h2>
        <p className="mt-2 text-[15px] text-white/50">No credit card required.</p>

        <ul className="mt-8 space-y-3.5">
          {BENEFITS.map((b) => (
            <li key={b} className="flex items-start gap-3">
              <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full" style={{ background: "rgba(124,107,255,0.18)" }}>
                <Check className="h-3 w-3" style={{ color: ACCENT }} />
              </span>
              <span className="text-[14.5px] text-white/75">{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="relative mt-12 pb-4">
        <PinlayrPreview />
      </div>
    </div>
  );
}

// Split-screen auth layout. `footer` renders under the form (e.g. sign-in link).
export default function AuthShell({ children, footer, altAction }) {
  return (
    <div className="min-h-screen w-full bg-[#151A24] lg:grid lg:grid-cols-[minmax(0,55fr)_minmax(0,45fr)]">
      {/* Discreet link into the Pinlayr widget demo harness (/demo/*) */}
      <Link
        to="/demo/landing"
        className="fixed bottom-4 left-4 z-40 inline-flex items-center gap-1.5 rounded-full border border-[#2A3040] bg-[#151A24]/90 px-3 py-1.5 text-[12px] font-medium text-[#98A0B0] shadow-sm backdrop-blur transition-colors hover:border-[#39415A] hover:text-[#F4F5F8]"
        data-testid="widget-demo-link"
      >
        <span className="grid h-4 w-4 place-items-center rounded text-[9px] font-bold" style={{ background: ACCENT, color: "#171717" }}>P</span>
        See the Pinlayr widget demo
        <span aria-hidden="true">&rarr;</span>
      </Link>

      {/* Left: account area */}
      <div className="flex min-h-screen flex-col px-6 py-8 sm:px-10 lg:px-14">
        <div className="flex items-center justify-between">
          <Logo />
          {altAction && (
            <div className="text-[13px] text-[#98A0B0]">
              {altAction.label}{" "}
              <Link to={altAction.to} className="font-semibold text-[#F4F5F8] underline-offset-2 hover:underline">
                {altAction.cta}
              </Link>
            </div>
          )}
        </div>

        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-[400px]">{children}</div>
        </div>

        {footer && <div className="text-center text-[13px] text-[#98A0B0]">{footer}</div>}
      </div>

      {/* Right: product panel (desktop) */}
      <ProductPanel />

      {/* Mobile condensed trial strip */}
      <div className="border-t border-[#242A38] bg-[#111111] px-6 py-8 lg:hidden">
        <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT }}>
          14-day free trial
        </span>
        <p className="mt-2 text-[17px] font-semibold text-[#F4F5F8]">Try Pinlayr free — no credit card required.</p>
        <ul className="mt-4 space-y-2">
          {BENEFITS.map((b) => (
            <li key={b} className="flex items-center gap-2.5 text-[13.5px] text-white/70">
              <Check className="h-4 w-4 shrink-0" style={{ color: ACCENT }} /> {b}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
